import React, {useRef, useState} from 'react'
import Reflux from 'reflux-react-16'
import moment from 'moment'

import OrderStore from '../stores/OrderStore.js'
import OrderActions from '../actions/OrderActions.js'
import geolocation from '../utils/geolocation.js'
import calcDistance from '../../utils/calcDistance.js'

import {Button, Card, Colors, H3, IconButton, P, Popup, Row, Spinner, Panel, H5, Badge, H4} from '../../src/components/UI/index.js'
import {emit} from 'messenger/socketio/socketEmit.js'
import {formatDuration} from '@utils/utils.js'

import {Activity, Reseller} from '../../server/functions/resellers/resellers.types.js'
import {User} from '../../server/functions/users/users.types.js'
import {BrandingObject} from '../../server/functions/login/login.types.js'
import {Customer, Location, Route, Shift} from 'types/types.js'
import time from 'server/utils/time.js'
import ShiftActivityPopup from '@components/ShiftActivityPopup.js'
import {SelectShiftPopup} from '../components/index.js'
import t from 'src/helpers/Languages.js'
import {formSpacing} from '../../src/components/UI/forms/index.js'

interface ShiftOverviewProps {
    reseller: Reseller
    user: User
    customers: Record<string, Customer>
    history: string[]
    branding: BrandingObject
    shift: Shift
    shifts: Shift[]
    selectedShift: string
    location?: Location
    loading: boolean
    routes: Record<string, Route>
}

function ShiftOverview({history, reseller, customers, branding, shift, shifts, selectedShift, location, loading, routes}: ShiftOverviewProps) {
    const popupRef = useRef(null)
    const shiftActivityPopupRef = useRef(null)
    const selectShiftPopup = useRef(null)
    const [shiftActionBusy, setShiftActionBusy] = useState(false)

    const onClickSelectShift = async () => {
        if (!selectShiftPopup.current) return

        const newSelectedShift = await selectShiftPopup.current.open(shifts, selectedShift)

        // undefined: closed without choosing
        if (newSelectedShift && newSelectedShift !== selectedShift) {
            OrderActions.selectShift(newSelectedShift)
        }
    }

    // Shows an error of a shift action in a popup with the title of that action
    const showError = (title: string, err) => {
        popupRef.current?.close()
        popupRef.current?.setError(err?.message || err?.toString())
        popupRef.current?.open(title, '')
    }

    const onClickStart = async () => {
        try {
            const hasActiveShift = shifts.some((s) => s._id !== shift?._id && s.date === shift?.date && s.started && !s.completed)

            if (hasActiveShift) {
                popupRef.current.setError(t('shiftStartBlockedByActiveShift'))
                if (!await popupRef.current.open(t('startShiftTitle'), '')) return
                return
            }

            if (reseller.settings.shifts.gpsRequired) {
                await geolocation.checkPermissions()
            }

            const startShiftBeforeStartTime = shift?.startShiftBeforeStartTime || reseller.settings.shifts.startShiftBeforeStartTime

            if (shift?.plannedStartTime && startShiftBeforeStartTime !== 'allowed') {
                const margin = startShiftBeforeStartTime === 'allowedmargin' ?
                    (shift?.startShiftBeforeStartTime ? shift.startShiftBeforeStartTimeMargin : reseller.settings.shifts.startShiftBeforeStartTimeMargin) || 0 :
                    0
                const earliestStartTime = moment(`${shift.date} ${shift.plannedStartTime}`, 'YYYY-MM-DD HH:mm').subtract(margin, 'minutes')

                if (earliestStartTime.isAfter(moment())) {
                    popupRef.current.setError(t('shiftStartTooEarly', earliestStartTime.format('HH:mm')))
                    if (!await popupRef.current.open(t('startShiftTitle'), '')) return
                    return
                }
            }

            if (reseller.settings.shifts.deviateStartLocation && shift?.startPoint) {
                if (!location?.lat) {
                    location = await geolocation.getCurrentPosition(reseller.settings.shifts.gpsRequired)
                }

                if (location) {
                    const distance = calcDistance(location, shift?.startPoint.position)

                    const isOutsideRadius = distance > (reseller.settings.shifts.maxStartEndLocationDeviation || 100) + location.accuracy

                    if (reseller.settings.shifts.deviateStartLocation === 'notallowed' && isOutsideRadius) {
                        popupRef.current.setError(t('shiftStartOutsideRadiusNotAllowed', reseller.settings.shifts.maxStartEndLocationDeviation || 100))
                        if (!await popupRef.current.open(t('startShiftTitle'), '')) return
                        return
                    }

                    if (reseller.settings.shifts.deviateStartLocation === 'warning' && isOutsideRadius) {
                        popupRef.current.setWarning(t('shiftStartOutsideRadiusWarning', reseller.settings.shifts.maxStartEndLocationDeviation || 100))
                        if (!await popupRef.current.open(t('startShiftTitle'), t('shiftStartConfirm'))) return
                    }
                }
            }

            if (!reseller.settings.shifts.activities?.length) {
                popupRef.current.setError(t('shiftStartNoActivities'))
                if (!await popupRef.current.open(t('startShiftTitle'), '')) return
                return
            }

            let activityId = reseller.settings.shifts?.activities?.find((activity) => activity.trigger === 'other')?.id

            if (reseller.settings.shifts?.activities?.length === 1) {
                activityId = reseller.settings.shifts.activities[0].id
            }

            if (!activityId) {
                await popupRef.current.close()

                activityId = await shiftActivityPopupRef.current.open(shift)

                // Closed without choosing
                if (!activityId) {
                    return
                }
            }

            await emit('shifts.startShift', {id: shift?._id, activityId, location})
            popupRef.current.close()
            history.push('/koerier/route')
        } catch (err) {
            showError(t('startShiftTitle'), err)
        }
    }

    const onClickEnd = async () => {
        try {
            const hasActiveRoute = Object.values(routes || {}).some((route) => route.started && !route.completed)

            if (hasActiveRoute) {
                popupRef.current.setError(t('shiftEndBlockedByActiveRoute'))
                if (!await popupRef.current.open(t('endShift'), '')) return
                return
            }

            if (reseller.settings.shifts.gpsRequired) {
                await geolocation.checkPermissions()
            }

            if (reseller.settings.shifts.deviateEndLocation && shift?.endPoint) {
                if (!location?.lat) {
                    location = await geolocation.getCurrentPosition(true)
                }

                if (location) {
                    const distance = calcDistance(location, shift?.endPoint.position)

                    const isOutsideRadius = distance > (reseller.settings.shifts.maxStartEndLocationDeviation || 100) + location.accuracy

                    if (reseller.settings.shifts.deviateEndLocation === 'notallowed' && isOutsideRadius) {
                        popupRef.current.setError(t('shiftEndOutsideRadiusNotAllowed', reseller.settings.shifts.maxStartEndLocationDeviation || 100))
                        if (!await popupRef.current.open(t('endShift'), '')) return
                        return
                    }

                    if (reseller.settings.shifts.deviateEndLocation === 'warning' && isOutsideRadius) {
                        popupRef.current.setWarning(t('shiftEndOutsideRadiusWarning', reseller.settings.shifts.maxStartEndLocationDeviation || 100))
                        if (!await popupRef.current.open(t('endShift'), t('shiftEndConfirm'))) return
                    }
                }
            }

            await emit('shifts.endShift', {id: shift?._id, location})
            popupRef.current.close()
        } catch (err) {
            showError(t('endShift'), err)
        }
    }

    const onClickReopen = async () => {
        try {
            await emit('shifts.reopenShift', {id: shift?._id})
        } catch (err) {
            showError(t('reopenShift'), err)
        }
    }

    // The button shows a spinner and ignores taps until the action is done or failed, so it can't be sent twice
    const onClickShiftButton = async () => {
        if (shiftActionBusy) return

        setShiftActionBusy(true)

        try {
            if (!shift?.started) {
                await onClickStart()
            } else if (!shift?.completed) {
                await onClickEnd()
            } else {
                await onClickReopen()
            }
        } finally {
            setShiftActionBusy(false)
        }
    }

    const activitiesObject: Record<string, Activity> = {}
    reseller.settings.shifts?.activities?.map((activity) => {
        activitiesObject[activity.id] = activity
    })

    const shiftActivities = structuredClone(shift?.activities || {})

    // Add duration for current activity if there is one
    if (shift?.activityId) {
        const currentDuration = time.durationBetween(shift?.activityStartTime, time.timestamp())

        shiftActivities[shift?.activityId] = shiftActivities[shift?.activityId] || {duration: 0}
        shiftActivities[shift?.activityId].duration += currentDuration
    }

    let pausedTime = shift?.pausedTime || 0

    if (shift?.pausedAt) {
        const currentPauseDuration = time.durationBetween(shift?.pausedAt, time.timestamp())

        pausedTime += currentPauseDuration
    }

    const currentTime = time.timestamp()
    const timeSinceEnd = time.durationBetween(shift?.endTime, currentTime, 'minutes')

    return (
        <>
            <div style={{width: '100%', flex: 1, height: 'calc(100% - 50px)', display: 'flex', flexDirection: 'column', overflowY: 'auto'}}>
                <Panel style={{display: 'flex', gap: formSpacing.tight, width: '100%', padding: '12px 6px 12px 6px', borderTop: 'none', borderRight: 'none', borderLeft: 'none'}}>

                    <div style={{width: 36}}>
                        <IconButton
                            onClick={onClickSelectShift}
                            badge={shifts.filter((s) => s._id !== shift?._id && !s.completed).length}
                        >
                            <i className='mdi mdi-calendar-clock' />
                        </IconButton>
                    </div>

                    <Button
                        variant='solid'
                        color={branding?.color || Colors.brandColor}
                        style={{flex: 1}}
                        icon='mdi mdi-calendar-clock'
                        label={!shift?.started ? t('startShift') : !shift?.completed ? t('endShift') : t('reopenShift')}
                        disabled={!shift || (shift?.completed && timeSinceEnd > 60)}
                        loading={shiftActionBusy}
                        onClick={onClickShiftButton}
                    />

                    <div style={{width: 36}} />

                </Panel>

                {loading ?
                    <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1}}>
                        <Spinner color={Colors.textDark} />
                    </div> :

                    <div style={{width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 24, gap: 24}}>
                        <Row style={{padding: 12, alignItems: 'center', justifyContent: 'center', gap: 12, flexWrap: 'wrap'}}>

                            <H3 style={{marginBottom: 0}}>{shift ? shift?.name : t('noShift')}</H3>

                            {shift?.completed ?
                                <Badge variant='success'>{t('completed')}</Badge> :
                                shift && !shift?.started ?
                                    <Badge variant='white'>{t('notStarted')}</Badge> :
                                    null
                            }
                        </Row>

                        <Card shadow={true} style={{padding: 12, width: '100%'}}>
                            <Row>
                                <i className='mdi mdi-store-outline' />
                                <P style={{marginLeft: 12}}>{`${t('customer')}: ${customers[shift?.customerId]?.name || ''}`}</P>
                            </Row>

                            <Row>
                                <i className='mdi mdi-clock-outline' />
                                <P style={{marginLeft: 12}}>{`${t('startTime')}: ${shift?.plannedStartTime || '--:--'}`}</P>
                            </Row>

                            <Row>
                                <i className='mdi mdi-clock-outline' />
                                <P style={{marginLeft: 12}}>{`${t('endTime')}: ${shift?.plannedEndTime || '--:--'}`}</P>
                            </Row>

                            <Row>
                                <i className='mdi mdi-map-marker-outline' />
                                {shift?.startPoint ?
                                    <P style={{marginLeft: 12}}>{`${t('startLocation')}: ${shift?.startPoint?.street} ${shift?.startPoint?.nr}${shift?.startPoint?.addition}, ${shift?.startPoint?.city}`}</P> :
                                    <P style={{marginLeft: 12}}>{`${t('startLocation')}:`}</P>
                                }
                            </Row>

                            <Row>
                                <i className='mdi mdi-map-marker-outline' />
                                {shift?.endPoint ?
                                    <P style={{marginLeft: 12}}>{`${t('endLocation')}: ${shift?.endPoint?.street} ${shift?.endPoint?.nr}${shift?.endPoint?.addition}, ${shift?.endPoint?.city}`}</P> :
                                    <P style={{marginLeft: 12}}>{`${t('endLocation')}:`}</P>
                                }
                            </Row>
                        </Card>

                        {shift?.targets?.map((target, index) => {
                            if (target.type === 'stopsPerHour') {
                                return (
                                    <Row key={index} style={{alignItems: 'center', justifyContent: 'center', gap: 24, width: '100%'}}>
                                        {shift?.completed &&
                                            <Card shadow={true} style={{borderRadius: 12, padding: 12, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12}}>
                                                <H5 style={{marginBottom: 6}}>{t('stopsPerHour')}</H5>

                                                <H5 style={{marginBottom: 0, fontSize: 22, fontWeight: 400}}>{target.realized || 0}</H5>
                                            </Card>
                                        }

                                        <Card shadow={true} style={{borderRadius: 12, padding: 12, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12}}>
                                            <H5 style={{marginBottom: 6}}>{shift?.completed ? t('target') : t('stopsPerHourTarget')}</H5>

                                            <H5 style={{marginBottom: 0, fontSize: 22, fontWeight: 400}}>{target.value}</H5>
                                        </Card>
                                    </Row>
                                )
                            }

                            return null
                        })}

                        {shift?.instructions &&
                            <div style={{width: '100%'}}>
                                <H5 style={{marginBottom: 24}}>{t('instruction')}</H5>

                                <Card style={{padding: 6, width: '100%'}}>
                                    <P style={{whiteSpace: 'pre-wrap'}}>{shift?.instructions}</P>
                                </Card>
                            </div>
                        }

                        {(Object.entries(shiftActivities).length > 0 || pausedTime > 0) &&
                            <>
                                <H5 style={{marginBottom: 0, width: '100%'}}>{t('activities')}</H5>

                                <Row style={{paddingLeft: 6, paddingRight: 6, flexDirection: 'column', width: '100%', gap: 6, minWidth: 'auto'}}>

                                    {shift?.completed &&
                                        <Card shadow style={{display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center', padding: '8px 12px 8px 12px', borderRadius: 12}}>
                                            <P>{t('totalWorkedTime')}</P>
                                            <H4 style={{marginBottom: 0}}>{formatDuration(shift.workedTime)}</H4>
                                        </Card>
                                    }

                                    {Object.entries(shiftActivities).map(([activityId, entry]) => {
                                        const activity = activitiesObject[activityId]

                                        return (
                                            <div key={activityId} style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: 12, background: activity?.color, height: 36, borderRadius: 12}}>
                                                <P onDark={Colors.isDarkBackground(activity?.color)}>{activity?.name}</P>
                                                <P onDark={Colors.isDarkBackground(activity?.color)}>{formatDuration(entry.duration)}</P>
                                            </div>
                                        )
                                    })}

                                    {pausedTime > 0 &&
                                        <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: 12, background: Colors.grey40, height: 36, borderRadius: 12}}>
                                            <P>{t('pause')}</P>
                                            <P>{formatDuration(pausedTime)}</P>
                                        </div>
                                    }
                                </Row>
                            </>
                        }
                    </div>
                }
            </div>

            <Popup ref={popupRef} />
            <ShiftActivityPopup ref={shiftActivityPopupRef} reseller={reseller} />
            <SelectShiftPopup ref={selectShiftPopup} reseller={reseller} />
        </>
    )
}

export default class ShiftOverviewWrapper extends Reflux.Component {
    constructor(props) {
        super(props)

        // @ts-expect-error reflux-react-16 is not typed
        this.store = OrderStore
        // @ts-expect-error reflux-react-16 is not typed
        this.state = undefined
        // @ts-expect-error reflux-react-16 is not typed
        this.props = props
    }

    render() {
        return (
            // @ts-expect-error reflux-react-16 is not typed
            <ShiftOverview {...this.props} {...this.state} />
        )
    }
}
